import { ArrowUpRight } from 'lucide-react';
import useInView from '@/hooks/useInView';
import useNoHover from '@/hooks/useNoHover';

interface NewsCardProps {
  src: string;
  alt: string;
  title: string;
  excerpt: string;
  category: string;
  onOpen: () => void;
}

export default function NewsCard({
  src,
  alt,
  title,
  excerpt,
  category,
  onOpen,
}: NewsCardProps) {
  const noHover = useNoHover();
  const { ref, inView } = useInView<HTMLButtonElement>(0.35);

  // On touch devices there is no hover, so the card lights up on scroll instead:
  // when it crosses the vertical centre of the viewport it "turns on".
  const revealed = noHover && inView;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onOpen}
      aria-label={`Ver dica: ${title}`}
      className={`group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-lg border bg-background-50 text-left transition-all duration-700 ease-out hover:-translate-y-1.5 hover:border-primary-300 ${
        revealed ? '-translate-y-1.5 border-primary-300' : 'translate-y-0 border-background-200'
      }`}
    >
      <div className="relative h-52 w-full overflow-hidden bg-background-200">
        <img
          src={src}
          alt={alt}
          title={`${title} · Porta31 Condomínios`}
          loading="lazy"
          className={`h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105 ${
            revealed ? 'scale-105' : 'scale-100'
          }`}
        />
        <div
          className={`absolute inset-0 bg-gradient-to-t from-secondary-950/70 via-secondary-950/5 to-transparent transition-opacity duration-700 ${
            revealed ? 'opacity-100 group-hover:opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        />
        <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-background-50 dark:text-foreground-950">
          {category}
        </span>
        <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/90 text-primary-700 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          <ArrowUpRight className="h-5 w-5" />
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
          {title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 transition-colors duration-300 group-hover:text-primary-600">
          Ver dica
          <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
        </span>
      </div>
    </button>
  );
}